import { OrdersService } from '@services/orders.service';
import { Injectable } from '@angular/core';
import { Router, Route } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { NavLink } from '@classes/nav-link';
import { AuthService } from './auth.service';
import { Role } from '@classes/role';
import { OrderCreateModeService } from '@services/order-create-mode.service';

@Injectable({
  providedIn: 'root',
})
export class NavService {
  private navLinks: NavLink[] = [
    {
      route: '/orders/list',
      icon: 'list_alt',
      hasBadge: true,
      badge: 'unassignedToTreat',
      badgeNumber: 0,
      checkRoleClient: true,
      titleForClient: 'orders.titleForClient',
    },
    { route: '/history/list', icon: 'list_alt' },
    { route: '/advancedSearch', icon: 'search' },
    { route: '/users', icon: 'group' },
    {
      route: '/repository',
      icon: 'local_library',
      children: [
        { route: '/repository/sites', icon: 'place' },
        { route: '/repository/addresses', icon: 'map' },
        { route: '/repository/categories', icon: 'category' },
        { route: '/repository/productTypes', icon: 'science' },
        { route: '/repository/clients', icon: 'store' },
        { route: '/repository/containers', icon: 'inventory_2' },
      ],
    },
    {
      route: '/vehicles',
      icon: 'drive_eta',
      children: [
        {
          route: '/vehicles/list',
          icon: 'pin',
          title: 'vehicles.vehicles-list.list.title',
        },
        {
          route: '/vehicles/history',
          icon: 'history',
          title: 'vehicles.history.title.simple',
        },
        {
          route: '/vehicles/reports',
          icon: 'car_crash',
          title: 'vehicles.reports.title.simple',
        },
      ],
    },
    { route: '/contacts', icon: 'call' },
    {
      route: '/billing',
      icon: 'money',
      children: [
        { route: '/billing/holiday-groups', icon: 'event' },
        { route: '/billing/holidays', icon: 'date_range' },
        { route: '/billing/schedules', icon: 'schedule' },
        { route: '/billing/export-list', icon: 'file_download' },
        { route: '/billing/articles', icon: 'euro' },
        { route: '/billing/waiting-times', icon: 'timer' },
      ],
    },
    { route: '/messages', icon: 'message' },
    { route: '/documents', icon: 'attach_file' },
    {
      route: '/recurring-orders',
      icon: 'pending_actions',
      children: [
        { route: '/recurring-orders/create', icon: 'add_circle' },
        { route: '/recurring-orders/list', icon: 'list_alt' },
      ],
    },
    { route: '/timeTracking', icon: 'schedule' },
  ];

  private headerNavLinks: NavLink[] = [
    {
      route: '/orders/create',
      icon: 'playlist_add',
      checkRoleClient: true,
      titleForClient: 'orders.create.titleForClient',
    },
    {
      route: '/orders/transmission/create',
      icon: 'send',
      checkRoleClient: true,
      titleForClient: 'orders.createTransmission.titleForClient',
    },
    // { route: '/simplifiedOrderCreate', icon: 'add' },
    { route: '/contact', icon: 'contact_mail' },
  ];

  constructor(
    private router: Router,
    private authSrv: AuthService,
    private ordersSrv: OrdersService,
    private orderCreateModeSrv: OrderCreateModeService,
  ) {
    this.navLinks = this.configureLinks();
    this.headerNavLinks = this.configureHeaderLinks();
  }

  /**
   * Return hierarchical nav links based on auth status and user role
   * Used in main menu and home page
   * Can be called without any param
   */
  getNavLinks(links?: NavLink[]): NavLink[] {
    links = links || JSON.parse(JSON.stringify(this.navLinks));
    return links
      .filter((link) => this.authSrv.isAuthorized(link.authorized, link.module))
      .map((link) => {
        if (link.children && link.children.length) {
          link.children = this.getNavLinks(link.children);
          // Remove hierarchy if only one child
          if (link.children.length === 1) {
            link = link.children[0];
          }
        }

        if (link.checkRoleClient) {
          if (this.authSrv.getDecodedToken().role.includes('client')) {
            link.title = link.titleForClient;
          }
        }

        return link;
      });
  }

  getHeaderNavLinks(links?: NavLink[]): NavLink[] {
    links = links || JSON.parse(JSON.stringify(this.headerNavLinks));
    return links
      .filter((link) => this.authSrv.isAuthorized(link.authorized, link.module))
      .map((link) => {
        if (link.children && link.children.length) {
          link.children = this.getHeaderNavLinks(link.children);
          // Remove hierarchy if only one child
          if (link.children.length === 1) {
            link = link.children[0];
          }
        }

        if (link.checkRoleClient) {
          if (this.authSrv.getDecodedToken().role.includes('client')) {
            link.title = link.titleForClient;
          }
        }

        return link;
      });
  }

  getHeaderNavLinks$(): Observable<NavLink[]> {
    return this.orderCreateModeSrv.getAccess().pipe(
      map(access => this.getHeaderNavLinks().filter(link => {
        if (link.route === '/orders/create') {
          return access.hasTransportSites;
        }

        if (link.route === '/orders/transmission/create') {
          return access.hasTransmissionSites;
        }

        return true;
      }))
    );
  }

  /**
   * Return NavLink object from array of navigations points
   * Return null if user is not allowed on this route or if the route does'nt exists
   * @param path: array of navigations points from parent to selected child
   * @param icon: code of showed icon
   */
  getLinkFromRoute(path: string[], icon: string): NavLink {
    const routeFound = this.getRouteFromPath(path);

    if (!routeFound) {
      return null;
    }

    const fullPath = '/' + path.join('/');
    return new NavLink(fullPath, routeFound.data.title, icon);
  }

  /**
   * Return corresponding route, if allowed, from array of navigations points
   * Check user role and authentication
   * By default, return null if user is not allowed/authenticated or if the route does'nt exists
   * @param path: array of navigations points from parent to selected child
   * @param testRole: if true (default), return null if user role is not allowed
   */
  private getRouteFromPath(
    path: string[],
    testRole = true,
    routes?: Route[],
    parentRoles?: Role[],
    index = 0,
  ): Route {
    routes = routes || this.router.config;

    return routes.reduce((found, current) => {
      // If already found in previous loop
      if (found) {
        return found;
      }

      if (parentRoles && !current.data.roles) {
        current.data.roles = parentRoles;
      }

      if (
        current.path !== path[index] ||
        (testRole && !this.authSrv.isAuthorized(current.data.roles))
      ) {
        return null;
      }

      // If last point of path's array
      if (!path[index + 1]) {
        return current;
      }

      if (current.children && current.children.length) {
        return this.getRouteFromPath(
          path,
          testRole,
          current.children,
          current.data.roles,
          index + 1,
        );
      }
    }, null);
  }

  /**
   * Get array of nav links. Generate texts and add roles based on router config
   * Used to manage route texts and role in only one place
   * @param links: NavLink[]
   */
  private configureLinks(links?: NavLink[]): NavLink[] {
    links = links || this.navLinks;
    return links.map((link) => {
      const splittedRoute = link.route.split('/').slice(1);
      const routeFound = this.getRouteFromPath(splittedRoute, false);

      if (routeFound && routeFound.data) {
        link.authorized = routeFound.data.roles || null;
        link.module = routeFound.data.module || null;
        if (routeFound.data.checkRole) {
          let decodedToken = this.authSrv.getDecodedToken();

          if (decodedToken.role.includes('client')) {
            link.title = routeFound.data.titleForClient;
          } else {
            link.title = routeFound.data.title;
          }
        } else {
          link.title = routeFound.data.title;
        }
      }

      if (link.children) {
        link.children = this.configureLinks(link.children);
      }

      return link;
    });
  }

  private configureHeaderLinks(links?: NavLink[]): NavLink[] {
    links = links || this.headerNavLinks;

    return links.map((link) => {
      const splittedRoute = link.route.split('/').slice(1);
      const routeFound = this.getRouteFromPath(splittedRoute, false);

      if (routeFound && routeFound.data) {
        link.authorized = routeFound.data.roles || null;
        link.module = routeFound.data.module || null;
        link.title = routeFound.data.title;
      }

      if (link.children) {
        link.children = this.configureLinks(link.children);
      }

      return link;
    });
  }
}
